
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Brain, 
  Users, 
  TrendingUp, 
  AlertCircle, 
  BarChart3, 
  Activity,
  Target,
  Zap,
  BookOpen,
  Clock,
  Database,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { aiAnalyticsService, RealAIInsight, AISystemMetrics } from '@/services/aiAnalyticsService';

const AIAnalyticsDashboard: React.FC = () => {
  const [insights, setInsights] = useState<RealAIInsight[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<AISystemMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    setIsLoading(true);
    try {
      const [metricsData, insightsData] = await Promise.all([
        aiAnalyticsService.getSystemMetrics(),
        aiAnalyticsService.getAdminInsights()
      ]);
      
      setSystemMetrics(metricsData);
      setInsights(insightsData);
    } catch (error) {
      console.error('Error loading AI analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDataFreshnessIndicator = (freshness: string) => {
    switch (freshness) {
      case 'fresh':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'stale':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'performance': return <TrendingUp className="h-4 w-4" />;
      case 'engagement': return <Users className="h-4 w-4" />;
      case 'content': return <BookOpen className="h-4 w-4" />;
      case 'prediction': return <Target className="h-4 w-4" />;
      default: return <Brain className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-blue-600" />
            AI Analytics Dashboard
            {systemMetrics && (
              <Badge variant={systemMetrics.hasRealData ? "default" : "secondary"} className="ml-2">
                {systemMetrics.hasRealData ? "Live Data" : "No Data"}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground">Real-time machine learning insights and analytics</p>
          {systemMetrics && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-muted-foreground">Data Status:</span>
              {getDataFreshnessIndicator(systemMetrics.dataFreshness)}
              <span className="text-sm">
                {systemMetrics.dataFreshness === 'fresh' && 'Fresh (< 24h)'}
                {systemMetrics.dataFreshness === 'stale' && 'Stale (> 24h)'}
                {systemMetrics.dataFreshness === 'none' && 'No data available'}
              </span>
            </div>
          )}
        </div>
        <Button onClick={loadAnalyticsData} disabled={isLoading}>
          {isLoading ? (
            <>
              <Activity className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <Zap className="h-4 w-4 mr-2" />
              Refresh Data
            </>
          )}
        </Button>
      </div>

      {/* AI System Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Insights</p>
                <p className="text-2xl font-bold">{systemMetrics?.totalInsights || 0}</p>
                {!systemMetrics?.hasRealData && (
                  <p className="text-xs text-muted-foreground">No insights generated yet</p>
                )}
              </div>
              <Brain className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Models</p>
                <p className="text-2xl font-bold">{systemMetrics?.activeModels || 0}</p>
                {!systemMetrics?.hasRealData && (
                  <p className="text-xs text-muted-foreground">No learning patterns detected</p>
                )}
              </div>
              <Target className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Accuracy Rate</p>
                <p className="text-2xl font-bold">
                  {systemMetrics?.accuracyRate || 0}%
                </p>
                {!systemMetrics?.hasRealData && (
                  <p className="text-xs text-muted-foreground">No accuracy data available</p>
                )}
              </div>
              <BarChart3 className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Last Analysis</p>
                <p className="text-sm font-bold">{systemMetrics?.lastAnalysis || 'Never'}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Real AI Insights */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">AI-Generated Insights</h3>
        
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-muted rounded"></div>
                    <div className="h-3 bg-muted rounded w-2/3"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : insights.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {insights.map((insight) => (
              <Card key={insight.id} className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(insight.type)}
                      <h4 className="font-semibold">{insight.title}</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getImpactColor(insight.impact)}>
                        {insight.impact} impact
                      </Badge>
                      {insight.actionable && (
                        <Badge variant="outline" className="text-green-600">
                          Actionable
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">{insight.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span>Confidence Level</span>
                      <span className="font-medium">{insight.confidence.toFixed(1)}%</span>
                    </div>
                    <Progress value={insight.confidence} className="h-1.5" />
                  </div>
                  
                  {insight.user_count && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Affects {insight.user_count} users
                    </div>
                  )}
                  
                  {insight.actionable && (
                    <div className="mt-4 pt-3 border-t">
                      <Button size="sm" variant="outline" className="w-full">
                        View Recommendations
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No AI Insights Available</h3>
              <p className="text-muted-foreground mb-4">
                AI insights will appear here as users interact with courses and generate learning data. 
                Start by having users complete courses and quizzes to generate meaningful analytics.
              </p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>To generate insights, ensure:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Users are enrolled in courses</li>
                  <li>Learning activities are being tracked</li>
                  <li>Quiz attempts are being recorded</li>
                  <li>User progress is being monitored</li>
                </ul>
              </div>
              <Button onClick={loadAnalyticsData} className="mt-4" variant="outline">
                <Activity className="h-4 w-4 mr-2" />
                Check for New Data
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AIAnalyticsDashboard;
